/* src/core/events.ts — the typed pub/sub bus (PLAN.md Part 2 §1, TASKS.md 0.6).
 *
 * This is the only channel across module boundaries. `net/`, `map/`, `render/`, `world/`
 * and `sim/` never import from `ui/`; when one needs to tell another something, it goes
 * through here or through core/store.ts. That boundary is what lets the render loop and the
 * DOM UI evolve independently without a component framework binding them together.
 *
 * Add events to `AppEvents` as phases land. The map is the documentation.
 */

import type { ConnState } from './types.ts';

export interface AppEvents {
  /** Weighted load progress for ui/loadingScreen.ts. `ratio` is 0..1. */
  'boot:progress': { readonly phase: string; readonly ratio: number };
  /** First interactive frame reached. */
  'boot:ready': undefined;
  /** Supabase reachability changed. Emitted by net/, rendered by ui/connStatus.ts. */
  'conn:change': ConnState;
  /** A still right-click / long-press resolved to a context menu (PLAN.md Part 4 §5). */
  'ui:contextmenu': { readonly x: number; readonly y: number; readonly target: HTMLElement };
  /** Pointer went down on a drag surface (window titlebar, resize handle). */
  'ui:dragstart': {
    readonly x: number;
    readonly y: number;
    readonly pointerId: number;
    readonly target: HTMLElement;
    readonly kind: string;
  };
  /** Escape pressed. Topmost dismissible surface should close and restore focus. */
  'ui:escape': undefined;
  /**
   * The player clicked their cart in the 3D world. Emitted by world/, handled by ui/ — the
   * render side never imports the window code, and the UI never imports the scene.
   */
  'world:cartclick': { readonly cartId: string };
  /** Pointer moved over the ground while placing a cart. */
  'world:placehover': { readonly lon: number; readonly lat: number };
  /** Player clicked a spot to place their cart. */
  'world:placepick': { readonly lon: number; readonly lat: number };
  /**
   * The player clicked a building. `osmWayId` comes off the picked triangle's own vertex data,
   * so it identifies the exact footprint that was hit rather than "something near here" — the
   * lon/lat is carried too, as the fallback for a hit whose feature id did not survive.
   */
  'world:buildingclick': {
    readonly osmWayId: number | null;
    readonly lon: number;
    readonly lat: number;
  };
  /**
   * Outline a footprint in the world, or clear it with an empty ring. Emitted by the building
   * inspector, drawn by world/ — the UI names a shape, the render side decides how to draw it.
   */
  'world:highlight': { readonly ring: ReadonlyArray<readonly [number, number]> };
  /**
   * The z14 survey cells the camera can currently see, nearest first, plus the viewpoint.
   * Emitted (throttled) by world/, consumed by main.ts, which decides which cells need a
   * survey — the render side never talks to the network about coverage.
   */
  'world:viewcells': {
    readonly lon: number;
    readonly lat: number;
    readonly cells: ReadonlyArray<readonly [number, number]>;
  };
  /** The camera jumped to a new part of the country (travel window, seam). */
  'world:traveled': { readonly lon: number; readonly lat: number; readonly name?: string };
  /** The view crossed between the flat world and the globe. */
  'world:globe': { readonly on: boolean };
  /** Surveyed-cell centres for the globe's gold dots. Emitted by main.ts on globe entry. */
  'globe:dots': { readonly points: ReadonlyArray<readonly [number, number]> };
  /** Survey progress for the HUD pill: how many visible cells are still being worked. */
  'world:surveys': { readonly pending: number; readonly fresh: number };
  /**
   * Step inside an owned building and walk it. Emitted by the inspector, handled by world/
   * (which hands it to render/interiorView.ts). Everything the interior needs to build itself
   * travels in the payload — the footprint, the real floor count, which floors stairs reach,
   * where the street is, and what is installed — so the render side never reaches back into the
   * UI or the network for it.
   */
  'world:enterinterior': InteriorDescriptor;
  /**
   * The building changed while the player is inside it (a fixture bought, a shelf stocked).
   * Re-point the interior at fresh data without moving the player. Emitted by ui/, handled by
   * render/interiorView.ts.
   */
  'interior:refresh': InteriorDescriptor;
  /** Leave the interior and return to the city view. Emitted by the interior HUD or a key. */
  'interior:exit': undefined;
  /** The interior HUD (or a floor key) asks to stand on a floor, 0-based. Handled by render/. */
  'interior:setfloor': { readonly floor: number };
  /**
   * Interior mode's current state, for ui/hud/interiorHud.ts. `active: false` closes the HUD;
   * otherwise it carries exactly what the chrome renders. Emitted by render/interiorView.ts —
   * the walk mode owns the truth of which floor you are on, the HUD only draws it.
   */
  'interior:state': InteriorState;
  /**
   * What the player's crosshair is resting on inside the building, or null between fixtures.
   * The HUD turns it into an "E — …" prompt; render/ emits and never renders text itself.
   */
  'interior:aim': { readonly fixtureId: string; readonly kind: string } | null;
  /** The player pressed the interact key on a fixture. ui/ opens the matching panel. */
  'interior:interact': { readonly fixtureId: string; readonly kind: string };
}

/** A fixture as the interior renders it — mirrors core/types.ts Fixture, restated here because
 *  core/events.ts must not import game types (it is beneath them in the dependency order). */
export interface InteriorFixture {
  readonly id: string;
  readonly kind: 'shelf' | 'checkout' | 'storage' | 'stairs';
  readonly floor: number;
  readonly slot: number;
  readonly itemId: string | null;
  readonly qty: number;
}

/** What render/interiorView.ts needs to build and label a walkable building. */
export interface InteriorDescriptor {
  readonly parcelId: string;
  readonly name: string;
  readonly floors: number;
  /** Ground plus one per consecutive staircase — the server's count, never inferred here. */
  readonly reachableFloors: number;
  /** The building's exterior ring as `[lon, lat]` pairs — the room is shaped from it. */
  readonly ring: ReadonlyArray<readonly [number, number]>;
  readonly lon: number;
  readonly lat: number;
  /** OSM way id, for hiding this building's exterior when looking out of its own windows. */
  readonly osmWayId: number | null;
  /** Bearing to the nearest road, degrees clockwise from north; the door goes on that wall. */
  readonly frontageDeg: number | null;
  readonly fixtures: ReadonlyArray<InteriorFixture>;
  /**
   * The server's live walk-in rate for this shop, customers per minute. Drives the VISIBLE
   * shoppers only — the money they appear to spend was already computed server-side; the little
   * people are a rendering of that number, never a source of it.
   */
  readonly customersPerMin: number;
}

/** The interior HUD's view of the world: closed, or open on a named building at a floor. */
export type InteriorState =
  | { readonly active: false }
  | {
      readonly active: true;
      readonly parcelId: string;
      readonly name: string;
      readonly floors: number;
      readonly reachableFloors: number;
      /** 0-based index of the floor the player is standing on. */
      readonly floor: number;
    };

type Handler<K extends keyof AppEvents> = (payload: AppEvents[K]) => void;

type ErasedHandler = (payload: never) => void;

/*
 * The registry is keyed by event name with erased payload types. `on`/`emit` are the typed
 * doors; nothing outside this file sees the erased map, so the erasure cannot leak. The
 * handler type is `(payload: never) => void` rather than `any` — it is assignable from every
 * concrete handler and callable only after the typed `emit` has narrowed the payload, so
 * CLAUDE.md's no-`any` rule holds.
 */
const registry = new Map<keyof AppEvents, Set<ErasedHandler>>();

/** Subscribe. Returns an unsubscribe function — call it, or leak a listener. */
export function on<K extends keyof AppEvents>(type: K, handler: Handler<K>): () => void {
  let handlers = registry.get(type);
  if (!handlers) {
    handlers = new Set();
    registry.set(type, handlers);
  }
  const erased = handler as ErasedHandler;
  handlers.add(erased);
  return () => {
    handlers.delete(erased);
  };
}

/** Subscribe for exactly one emission. */
export function once<K extends keyof AppEvents>(type: K, handler: Handler<K>): () => void {
  const off = on(type, (payload) => {
    off();
    handler(payload);
  });
  return off;
}

export function emit<K extends keyof AppEvents>(
  type: K,
  ...args: AppEvents[K] extends undefined ? [] : [payload: AppEvents[K]]
): void {
  const handlers = registry.get(type);
  if (!handlers || handlers.size === 0) return;

  const payload = args[0] as never;
  // Copy first: a handler is allowed to unsubscribe itself, or subscribe another.
  for (const handler of [...handlers]) {
    // One bad subscriber must not abort delivery to the rest — CLAUDE.md's degrade-locally
    // rule applies to the bus as much as to a failing world-data domain.
    try {
      handler(payload);
    } catch (err) {
      console.error(`[events] handler for "${String(type)}" threw`, err);
    }
  }
}

/** Test seam. Never call this from app code. */
export function resetBus(): void {
  registry.clear();
}
